/**
 * The connection.
 *
 * The client folds the very same events the server folds, with the same
 * reducer -- it is sent redacted events, never a rendered view, so there is
 * only one implementation of the rules in the whole system.
 *
 * Three pieces make up what is on screen:
 *
 *   base     the last snapshot the server sent
 *   log      redacted events since that snapshot
 *   pending  events predicted locally for an action not yet acknowledged
 *
 * Rendering folds all three. When the server acknowledges an action its
 * prediction is dropped, and the authoritative events take its place.
 */

import {
  EMPTY_STATE,
  deriveEvents,
  fold,
  indexMap,
  type Action,
  type ActionType,
  type GameEvent,
  type GameState,
  type MapIndex,
  type RecordedEvent,
  type SeatId,
  type Viewer,
  type Warning,
} from '@risk/engine';
import { classic42 } from '@risk/maps';
import type { PendingCorrection, SeatPresence, ServerMessage } from '@risk/protocol';

const PROTOCOL_VERSION = 1 as const;

export type ConnectionStatus = 'connecting' | 'open' | 'reconnecting' | 'offline';

export interface PendingWarning {
  readonly actionId: string;
  readonly action: Action;
  readonly warnings: readonly Warning[];
}

export interface ClientState {
  readonly status: ConnectionStatus;
  readonly gameId: string | null;
  readonly roomCode: string | null;
  readonly seatId: SeatId | null;
  readonly lastSeq: number;
  readonly game: GameState;
  readonly presence: readonly SeatPresence[];
  /** Corrections raised by anyone, waiting on a second player. */
  readonly proposals: readonly PendingCorrection[];
  /** Set when the server pushed back on an action; the UI offers to override. */
  readonly warning: PendingWarning | null;
  readonly notice: string | null;
}

/**
 * Actions safe to predict locally. Anything whose outcome depends on state
 * this client cannot see is excluded: predicting it wrongly and snapping back
 * looks exactly like an app that cheats.
 */
const PREDICTABLE: ReadonlySet<ActionType> = new Set<ActionType>([
  'place',
  'fortify',
  'adjustAudit',
  'declareAttack',
  'identifyCard',
  'advancePhase',
]);

const DEVICE_KEY = 'risk.deviceId';
const seatKey = (code: string): string => `risk.seat.${code}`;

function deviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_KEY);
    if (existing) return existing;
    const fresh = crypto.randomUUID() + crypto.randomUUID().slice(0, 8);
    localStorage.setItem(DEVICE_KEY, fresh);
    return fresh;
  } catch {
    // Private browsing, blocked storage: a per-session identity still works,
    // it just cannot survive a reload.
    return crypto.randomUUID() + crypto.randomUUID().slice(0, 8);
  }
}

export class GameClient {
  readonly index: MapIndex = indexMap(classic42);

  private socket: WebSocket | null = null;
  private listeners = new Set<() => void>();
  private snapshot: ClientState;

  private base: GameState = EMPTY_STATE;
  private log: RecordedEvent[] = [];
  private pending = new Map<string, readonly GameEvent[]>();
  private cached: GameState | null = null;

  private attempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private closedByUs = false;
  private readonly device: string;

  /**
   * `deviceId` is injectable so that two clients can be driven in one process
   * during tests. In the browser it comes from localStorage, which is exactly
   * what makes a second tab on the same phone reclaim the same seat.
   */
  constructor(options: { readonly deviceId?: string } = {}) {
    this.device = options.deviceId ?? deviceId();
    this.snapshot = {
      status: 'connecting',
      gameId: null,
      roomCode: null,
      seatId: null,
      lastSeq: 0,
      game: EMPTY_STATE,
      presence: [],
      proposals: [],
      warning: null,
      notice: null,
    };
  }

  // --- store plumbing ---------------------------------------------------

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): ClientState => this.snapshot;

  private set(patch: Partial<ClientState>): void {
    this.snapshot = { ...this.snapshot, ...patch };
    for (const listener of this.listeners) listener();
  }

  private get viewer(): Viewer {
    return this.snapshot.seatId ?? 'spectator';
  }

  /** Refold base + log + predictions, and publish the result. */
  private refresh(): void {
    const predicted: RecordedEvent[] = [];
    let seq = this.snapshot.lastSeq;
    for (const events of this.pending.values()) {
      for (const event of events) {
        seq += 1;
        predicted.push({ ...event, id: `pending-${seq}`, seq, ts: Date.now(), actor: this.snapshot.seatId } as RecordedEvent);
      }
    }
    this.cached = fold([...this.log, ...predicted], { viewer: this.viewer, initial: this.base });
    this.set({ game: this.cached });
  }

  // --- connection -------------------------------------------------------

  connect(): void {
    this.closedByUs = false;
    const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
    const socket = new WebSocket(`${scheme}://${location.host}/ws`);
    this.socket = socket;

    socket.onopen = () => {
      this.attempt = 0;
      this.startHeartbeat();
      this.set({ status: 'open', notice: null });
      this.send({ v: PROTOCOL_VERSION, t: 'hello', deviceId: this.device, ...this.resumePayload() });
      // If we already had a game, ask for anything missed while away.
      if (this.snapshot.gameId) this.send({ v: PROTOCOL_VERSION, t: 'resync', sinceSeq: this.snapshot.lastSeq });
    };

    socket.onmessage = (event: MessageEvent<string>) => {
      try {
        this.receive(JSON.parse(event.data) as ServerMessage);
      } catch {
        // A frame we cannot parse is not worth tearing the session down for.
      }
    };

    socket.onclose = () => {
      this.stopHeartbeat();
      if (this.closedByUs) return;
      this.set({ status: 'reconnecting' });
      this.scheduleReconnect();
    };

    socket.onerror = () => socket.close();
  }

  private resumePayload(): { resume?: { gameId: string; seatToken: string } } {
    const { gameId, roomCode } = this.snapshot;
    if (!gameId || !roomCode) return {};
    try {
      const token = localStorage.getItem(seatKey(roomCode));
      return token ? { resume: { gameId, seatToken: token } } : {};
    } catch {
      return {};
    }
  }

  /**
   * Keep the connection warm. A game can sit idle for minutes while people
   * argue, and a proxy in front of this will happily close a silent socket --
   * nginx gives up after 60 seconds by default.
   */
  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeat = setInterval(() => {
      this.send({ v: PROTOCOL_VERSION, t: 'ping', clientTime: Date.now() });
    }, 20_000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeat !== null) {
      clearInterval(this.heartbeat);
      this.heartbeat = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) return;
    this.attempt += 1;
    // Backoff with jitter, capped: a table full of phones waking at once
    // should not all retry on the same tick.
    const base = Math.min(15_000, 500 * 2 ** (this.attempt - 1));
    const delay = base * (0.5 + Math.random() / 2);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  /** Called when the tab becomes visible: phones suspend sockets on lock. */
  wake(): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.send({ v: PROTOCOL_VERSION, t: 'ping', clientTime: Date.now() });
      this.send({ v: PROTOCOL_VERSION, t: 'resync', sinceSeq: this.snapshot.lastSeq });
      return;
    }
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.attempt = 0;
    this.connect();
  }

  private send(message: unknown): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify(message));
  }

  // --- inbound ----------------------------------------------------------

  private receive(message: ServerMessage): void {
    switch (message.t) {
      case 'welcome':
        return;

      case 'joined': {
        this.base = message.snapshot;
        this.log = [];
        this.pending.clear();
        if (message.seatToken) {
          try {
            localStorage.setItem(seatKey(message.roomCode), message.seatToken);
          } catch {
            // Without storage the seat cannot be reclaimed after a reload;
            // the player picks it again, which needs a second confirmation.
          }
        }
        this.set({
          gameId: message.gameId,
          roomCode: message.roomCode,
          seatId: message.seatId,
          lastSeq: message.lastSeq,
        });
        this.refresh();
        return;
      }

      case 'events': {
        const fresh = message.events.filter((event) => event.seq > this.snapshot.lastSeq);
        this.log = [...this.log, ...fresh];
        this.set({ lastSeq: Math.max(this.snapshot.lastSeq, message.lastSeq) });
        this.refresh();
        return;
      }

      case 'snapshot': {
        // A correction refolds the log, so the server sends a whole state.
        this.base = message.state;
        this.log = [];
        this.pending.clear();
        this.set({
          lastSeq: message.lastSeq,
          notice: message.reason === 'amended' ? 'A correction was applied' : null,
        });
        this.refresh();
        return;
      }

      case 'ack':
        // Drop the prediction, and *do not* touch lastSeq.
        //
        // The ack carries the sequence the server reached, but the events
        // themselves arrive in the frame after it. Adopting that number here
        // made the `events` handler below filter those very events out as
        // already-seen, so an acting player's own moves never reached their
        // own screen -- they saw a prediction flash and vanish, while everyone
        // else saw the move land. lastSeq may only advance when content has
        // actually been applied.
        this.pending.delete(message.actionId);
        this.refresh();
        return;

      case 'warn':
        // The move did not happen. Drop the prediction and ask the player.
        this.pending.delete(message.actionId);
        this.set({
          warning: {
            actionId: message.actionId,
            action: this.lastAction.get(message.actionId) ?? { type: 'advancePhase' },
            warnings: message.warnings,
          },
        });
        this.refresh();
        return;

      case 'nack': {
        this.pending.delete(message.actionId);
        const attempted = this.lastAction.get(message.actionId);

        // A stale board is not the player's fault and not a reason to lose
        // their tap. The server has already sent the state we were missing,
        // so re-send once against it. Two quick taps -- assign, then begin --
        // otherwise had the second silently dropped.
        if (message.code === 'stale_state' && attempted && !this.retried.has(message.actionId)) {
          this.retried.add(message.actionId);
          queueMicrotask(() => this.act(attempted, true));
          this.refresh();
          return;
        }

        this.set({ notice: message.message });
        this.refresh();
        return;
      }

      case 'presence':
        this.set({ presence: message.seats });
        return;

      case 'proposals':
        this.set({ proposals: message.proposals });
        return;

      case 'pong':
        return;

      case 'error':
        this.set({ notice: message.message });
        return;

      default:
        return;
    }
  }

  // --- outbound ---------------------------------------------------------

  private lastAction = new Map<string, Action>();
  /** Actions already retried once, so a persistent refusal cannot loop. */
  private retried = new Set<string>();

  createRoom(preset?: 'classic1993' | 'revised2008'): void {
    this.send({ v: PROTOCOL_VERSION, t: 'room.create', ...(preset ? { preset } : {}) });
  }

  joinRoom(roomCode: string): void {
    this.send({ v: PROTOCOL_VERSION, t: 'room.join', roomCode: roomCode.trim().toUpperCase() });
  }

  claimSeat(name: string, color: string): void {
    this.send({ v: PROTOCOL_VERSION, t: 'seat.claim', name, color });
  }

  act(action: Action, override = false): void {
    const actionId = crypto.randomUUID();
    this.lastAction.set(actionId, action);

    if (PREDICTABLE.has(action.type) && this.snapshot.seatId) {
      // Predict locally so the UI responds at once; the server's version
      // replaces this the moment it acknowledges.
      const predicted = deriveEvents({
        state: this.snapshot.game,
        index: this.index,
        request: { action, actor: this.snapshot.seatId, override },
      });
      if (predicted.length > 0) this.pending.set(actionId, predicted);
      this.refresh();
    }

    this.send({
      v: PROTOCOL_VERSION,
      t: 'action',
      actionId,
      action,
      override,
      // Staleness check, but only while nothing is in flight. With a
      // prediction outstanding the client cannot know what sequence the
      // server will be at -- an override, for instance, appends an extra
      // event -- and a wrong guess would have the action dropped rather
      // than merely re-synced. Tapping armies out one at a time must never
      // lose a tap, so the check is skipped for exactly that window.
      expectedSeq: this.pending.size === 0 ? this.snapshot.lastSeq : 0,
    });
  }

  proposeCorrection(territory: string, owner: SeatId | null, armies: number, rationale: string): void {
    this.act({
      type: 'proposeCorrection',
      directive: { op: 'set', territory, owner, armies },
      rationale,
    });
  }

  confirmCorrection(proposalId: string): void {
    this.act({ type: 'confirmCorrection', proposalId });
  }

  rejectCorrection(proposalId: string): void {
    this.act({ type: 'rejectCorrection', proposalId });
  }

  /** The player saw the warning and asked to proceed anyway. */
  overrideWarning(): void {
    const warning = this.snapshot.warning;
    if (!warning) return;
    this.set({ warning: null });
    this.act(warning.action, true);
  }

  dismissWarning(): void {
    this.set({ warning: null });
  }

  dismissNotice(): void {
    this.set({ notice: null });
  }

  disconnect(): void {
    this.closedByUs = true;
    this.stopHeartbeat();
    this.socket?.close();
  }
}
